import React, { useState, useEffect } from 'react';
import { AppProvider, useAppContext } from './context/AppContext';
import { WebSocketProvider } from './context/WebSocketContext';
import LoginView from './components/auth/LoginView';
import WorkshopsView from './components/workshops/WorkshopsView';
import WorkshopDetailView from './components/workshops/WorkshopDetailView';
import SessionView from './components/session/SessionView';
import WritingSessionView from './components/writing/WritingSessionView';
import UserManagement from './components/admin/UserManagement';

const AppContent = () => {
  const { currentUser, isInitialized, presenterMode, sessionExpired } = useAppContext();
  const [activeView, setActiveView] = useState('login');
  const [activeWorkshop, setActiveWorkshop] = useState(null);
  const [workshopTab, setWorkshopTab] = useState('writing');
  const [activeWritingSession, setActiveWritingSession] = useState(null);

  useEffect(() => {
    if (activeView !== 'login') {
      localStorage.setItem('lastView', activeView);
      if (activeWorkshop) {
        localStorage.setItem('lastWorkshop', JSON.stringify(activeWorkshop));
      }
      localStorage.setItem('lastWorkshopTab', workshopTab);
      if (activeWritingSession) {
        localStorage.setItem('lastWritingSession', JSON.stringify(activeWritingSession));
      }
    }
  }, [activeView, activeWorkshop, workshopTab, activeWritingSession]);

  useEffect(() => {
    if (isInitialized && currentUser) {
      const savedView = localStorage.getItem('lastView');
      const savedWorkshop = localStorage.getItem('lastWorkshop');
      const savedWorkshopTab = localStorage.getItem('lastWorkshopTab');
      const savedWritingSession = localStorage.getItem('lastWritingSession');

      // Every view this version of the app actually knows how to render.
      // Anything else -- including legacy names like 'dashboard' or
      // 'writing-dashboard' saved by an older version of the app before this
      // update -- must fall back safely instead of rendering blank.
      const knownViews = ['workshops', 'workshop-detail', 'session', 'writing-session', 'user-management'];

      // Views that additionally require a workshop to be restored alongside them
      const workshopScopedViews = ['workshop-detail', 'session', 'writing-session'];

      if (savedView && savedView !== 'login' && knownViews.includes(savedView)) {
        if (workshopScopedViews.includes(savedView) && !savedWorkshop) {
          // Can't restore a workshop-scoped view without its workshop -- fall back
          setActiveView('workshops');
        } else {
          setActiveView(savedView);
          if (savedWorkshop) {
            try {
              setActiveWorkshop(JSON.parse(savedWorkshop));
              if (['foresight', 'writing', 'cross-section'].includes(savedWorkshopTab)) {
                setWorkshopTab(savedWorkshopTab);
              }
            } catch (error) {
              console.error('Failed to restore workshop:', error);
              setActiveView('workshops');
            }
          }
          if (savedView === 'writing-session' && savedWritingSession) {
            try {
              setActiveWritingSession(JSON.parse(savedWritingSession));
            } catch (error) {
              console.error('Failed to restore writing session:', error);
              setActiveView(savedWorkshop ? 'workshop-detail' : 'workshops');
            }
          }
        }
      } else if (savedView && savedView !== 'login' && !knownViews.includes(savedView)) {
        // Stale view name from an older version of the app -- clear it and
        // land on the new default instead of rendering nothing.
        localStorage.removeItem('lastView');
        localStorage.removeItem('lastWorkshop');
        localStorage.removeItem('lastWorkshopTab');
        localStorage.removeItem('lastWritingSession');
        setActiveView('workshops');
      } else if (activeView === 'login') {
        setActiveView('workshops');
      }
    } else if (isInitialized && !currentUser) {
      setActiveView('login');
      localStorage.removeItem('lastView');
      localStorage.removeItem('lastWorkshop');
      localStorage.removeItem('lastWorkshopTab');
      localStorage.removeItem('lastWritingSession');
    }
  }, [currentUser, isInitialized]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-[#01006A] flex items-center justify-center">
        <div className="text-white opacity-70 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className={presenterMode ? 'presenter-mode' : ''}>
      {activeView === 'login' && (
        <LoginView
          onLoginSuccess={() => setActiveView('workshops')}
          sessionExpired={sessionExpired}
        />
      )}
      {activeView === 'workshops' && (
        <WorkshopsView
          onNavigateToWorkshop={(workshop) => {
            setActiveWorkshop(workshop);
            setWorkshopTab('writing');
            setActiveView('workshop-detail');
          }}
          onNavigateToUserManagement={() => setActiveView('user-management')}
        />
      )}
      {activeView === 'workshop-detail' && activeWorkshop && (
        <WorkshopDetailView
          workshop={activeWorkshop}
          activeTab={workshopTab}
          onTabChange={setWorkshopTab}
          onNavigateBack={() => { setActiveWorkshop(null); setActiveView('workshops'); }}
          onNavigateToSession={() => setActiveView('session')}
          onNavigateToWritingSession={(session) => {
            setActiveWritingSession(session);
            setActiveView('writing-session');
          }}
        />
      )}
      {activeView === 'session' && (
        <SessionView onNavigateBack={() => setActiveView('workshop-detail')} />
      )}
      {activeView === 'writing-session' && activeWritingSession && (
        <WritingSessionView
          writingSession={activeWritingSession}
          onNavigateBack={() => { setActiveWritingSession(null); setActiveView('workshop-detail'); }}
        />
      )}
      {activeView === 'user-management' && (
        <UserManagement onNavigateBack={() => setActiveView('workshops')} />
      )}
    </div>
  );
};

const App = () => {
  return (
    <AppProvider>
      <WebSocketProvider>
        <AppContent />
      </WebSocketProvider>
    </AppProvider>
  );
};

export default App;
